import { Commodity } from "@/pages/sy/models/Commodity";
import { RecipeLayer } from "@/pages/sy/models/RecipeLayer";
import {
  useUpsertRecipeLayerMutation,
  useDeleteRecipeLayerMutation,
} from "@/pages/sy/hooks/queries/useRecipeMutations";
import { getErrorMessage } from "@/shared/hooks/getErrorMessage";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
  Button,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Autocomplete } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { AddMaterialButton } from "@/pages/sy/components/AddMaterialButton";

type Props = {
  layer: RecipeLayer;
  commodities: Commodity[];
  disabled: boolean;
  setNotificationMessage: (message: string) => void;
  initialEditMode?: boolean;
  onEditComplete?: () => void;
  totalWeight: number;
  isNewLayer?: boolean;
  onCancelNewLayer?: (layerId: number) => void;
  maxSelectableLayerNumber: number;
  onLayerNumberChange: (layer: RecipeLayer, newLayerNumber: number) => void;
  onAddMaterial?: (layerNumber: number) => void;
  siblingCommodityIds?: number[];
  /** Called when RECIPE_IN_USE error is detected - parent should show upgrade modal */
  onRecipeInUse?: (pendingLayerData: RecipeLayer) => void;
  isLastInGroup?: boolean;
};

export const RecipeLayerComponent = ({
  layer,
  commodities,
  disabled,
  setNotificationMessage,
  totalWeight,
  maxSelectableLayerNumber,
  onLayerNumberChange,
  onAddMaterial,
  siblingCommodityIds = [],
  onRecipeInUse,
  isLastInGroup = false,
}: Props) => {
  const weightInputRef = useRef<HTMLInputElement>(null);

  // === TanStack Query Mutations ===
  const upsertLayerMutation = useUpsertRecipeLayerMutation();
  const deleteLayerMutation = useDeleteRecipeLayerMutation();

  const [error, setError] = useState("");
  const onMutationError = (err: unknown) => setError(getErrorMessage(err));
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Combined loading state for UI (replaces manual isLoading state)
  const isLoading =
    upsertLayerMutation.isPending || deleteLayerMutation.isPending;

  const availableCommodities = commodities.filter((c) => c.IsEnable);
  const selectableCommodities = availableCommodities.filter(
    (c) => !siblingCommodityIds.some((id) => id === c.CommodityId),
  );
  const isSharedLayer = siblingCommodityIds.length > 0;

  const { control, reset, watch, getValues } = useForm<RecipeLayer>({
    mode: "onChange",
    defaultValues: layer,
  });

  const watchedWeight = watch("Weight");
  const watchedCommodityId = watch("CommodityId");

  const currentCommodity = useMemo(() => {
    return availableCommodities.find(
      (c) => c.CommodityId === watchedCommodityId,
    );
  }, [availableCommodities, watchedCommodityId]);

  const commodityColor = useMemo(() => {
    return currentCommodity?.CommodityColor || "#888888";
  }, [currentCommodity]);

  const percentage = useMemo(() => {
    if (!totalWeight || !watchedWeight) return 0;
    return (watchedWeight / totalWeight) * 100;
  }, [watchedWeight, totalWeight]);

  const getBgColorWithAlpha = (color: string, alpha = 0.15) => {
    if (color && color.startsWith("#")) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    } else if (color && color.startsWith("rgb(")) {
      return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
    }
    return `rgba(136, 136, 136, ${alpha})`;
  };

  const bgColor = getBgColorWithAlpha(commodityColor, 0.1);
  const inputBgColor = getBgColorWithAlpha(commodityColor, 0.15);

  const saveLayer = (data: RecipeLayer) => {
    // Validation: weight must be >= 0
    if (data.Weight < 0) return;

    if (!data.CommodityId && availableCommodities.length > 0) {
      data.CommodityId = availableCommodities[0].CommodityId;
    }

    upsertLayerMutation.mutate(data, {
      onSuccess: (resp) => {
        const result = resp.data?.tables?.[0]?.rows?.[0];
        // Check if recipe is in use - notify parent to show upgrade modal
        if (result?.ErrorCode === "RECIPE_IN_USE" && onRecipeInUse) {
          onRecipeInUse(data);
          return;
        }
        setNotificationMessage("Recipe layer saved successfully");
      },
      onError: onMutationError,
    });
  };

  const handleDelete = () => {
    if (!layer.RecipeLayerId) return;

    deleteLayerMutation.mutate(layer.RecipeLayerId, {
      onSuccess: () => {
        setNotificationMessage("Recipe layer deleted successfully");
      },
      onError: onMutationError,
      onSettled: () => setShowDeleteConfirm(false),
    });
  };

  const handleWeightBlur = () => {
    const formData = getValues();
    saveLayer(formData);
  };

  const handleWeightKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      weightInputRef.current?.blur();
    }
  };

  const handleCommodityChange = (
    _: React.SyntheticEvent,
    newValue: Commodity | null,
  ) => {
    const formData = getValues();
    if (newValue) {
      formData.CommodityId = newValue.CommodityId;
      saveLayer(formData);
    }
  };

  // Reset form when layer data changes (from server via SignalR refetch)
  useEffect(() => {
    reset(layer);
  }, [layer.RecipeLayerId, layer.CommodityId, layer.Weight, layer.LayerNumber]);

  return (
    <div
      style={{
        position: "relative",
        borderBottom: "1px solid #f0f0f0",
        backgroundColor: bgColor,
      }}
    >
      <div
        style={{
          position: "absolute",
          bottom: isLastInGroup ? "3px" : 0,
          left: 0,
          height: "3px",
          width: `${percentage}%`,
          backgroundColor: commodityColor,
          zIndex: 1,
        }}
      />

      <Grid container alignItems="center">
        <Grid
          item
          md={2}
          xs={2}
          style={{ padding: "8px 15px", textAlign: "center" }}
        >
          <Controller
            name="LayerNumber"
            control={control}
            render={({ field }) => (
              <FormControl variant="outlined" size="small" fullWidth>
                <Select
                  value={field.value}
                  onChange={(e) =>
                    onLayerNumberChange(layer, Number(e.target.value))
                  }
                  disabled={disabled}
                  inputProps={{
                    style: {
                      textAlign: "center",
                      color: "#000000",
                    },
                  }}
                  style={{
                    backgroundColor: "white",
                    borderColor: commodityColor,
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {Array.from(
                    { length: maxSelectableLayerNumber },
                    (_, i) => i + 1,
                  ).map((num) => (
                    <MenuItem key={num} value={num}>
                      {num}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item md={5} xs={5} style={{ padding: "8px 15px" }}>
          <Controller
            name="CommodityId"
            control={control}
            render={({ field }) => {
              return (
                <Autocomplete
                  id={`commodity-autocomplete-${layer.RecipeLayerId}`}
                  options={selectableCommodities}
                  getOptionLabel={(option) => {
                    if (typeof option === "object" && option !== null) {
                      return option.CommodityName || "";
                    }
                    const commodity = availableCommodities.find(
                      (c) => c.CommodityId === option,
                    );
                    return commodity ? commodity.CommodityName : "";
                  }}
                  value={
                    availableCommodities.find(
                      (c) => c.CommodityId === field.value,
                    ) || null
                  }
                  onChange={handleCommodityChange}
                  disabled={disabled}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      fullWidth
                      required
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          style: {
                            backgroundColor: disabled ? inputBgColor : "white",
                            borderColor: commodityColor,
                          },
                        },
                        htmlInput: {
                          ...params.inputProps,
                          style: {
                            color: "#000000",
                          },
                        },
                      }}
                    />
                  )}
                />
              );
            }}
          />
        </Grid>

        <Grid
          item
          md={2}
          xs={2}
          style={{ padding: "8px 15px", textAlign: "center" }}
        >
          <Controller
            name="Weight"
            control={control}
            render={({ field }) => {
              const [inputValue, setInputValue] = useState(
                field.value?.toString() || "",
              );

              useEffect(() => {
                setInputValue(field.value?.toString() || "");
              }, [field.value]);

              return (
                <TextField
                  variant="outlined"
                  value={inputValue}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setInputValue(newValue);
                    if (newValue === "") {
                    } else {
                      const value = parseFloat(newValue);
                      if (!isNaN(value)) {
                        field.onChange(value);
                      }
                    }
                  }}
                  onBlur={() => {
                    if (inputValue === "") {
                      setInputValue("0");
                      field.onChange(0);
                    }
                    handleWeightBlur();
                  }}
                  onKeyDown={handleWeightKeyDown}
                  size="small"
                  fullWidth
                  disabled={disabled}
                  type="number"
                  slotProps={{
                    input: {
                      style: {
                        backgroundColor: disabled ? inputBgColor : "white",
                        borderColor: commodityColor,
                      },
                    },
                    htmlInput: {
                      min: 0,
                      style: {
                        textAlign: "center",
                        color: "#000000",
                      },
                      ref: weightInputRef,
                    },
                  }}
                />
              );
            }}
          />
        </Grid>

        <Grid
          item
          md={1}
          xs={1}
          style={{ padding: "8px 15px", textAlign: "center" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "40px",
              color: "#1976d2",
              fontWeight: "bold",
            }}
          >
            {percentage.toFixed(2)}%
          </div>
        </Grid>

        <Grid
          item
          md={2}
          xs={2}
          style={{
            textAlign: "center",
            display: "flex",
            justifyContent: "space-evenly",
          }}
        >
          {onAddMaterial && layer.LayerNumber != null && (
            <AddMaterialButton
              layerNumber={layer.LayerNumber}
              disabled={disabled}
              onAddMaterial={onAddMaterial}
            />
          )}
          <IconButton
            onClick={() => setShowDeleteConfirm(true)}
            color="secondary"
            disabled={disabled}
            size="small"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Grid>
      </Grid>

      {/* Delete Layer Confirmation Modal */}
      <MesfModal
        title={
          isSharedLayer ? "CONFIRM DELETE MATERIAL" : "CONFIRM DELETE LAYER"
        }
        open={showDeleteConfirm}
        handleClose={() => setShowDeleteConfirm(false)}
        maxWidth="sm"
      >
        <MesfModal.Content sx={{ p: 4 }}>
          {isSharedLayer ? (
            <Typography variant="body1">
              Are you sure you want to remove{" "}
              <strong>
                {currentCommodity?.CommodityName ?? "this material"}
              </strong>{" "}
              from layer {layer.LayerNumber}? This action cannot be undone.
            </Typography>
          ) : (
            <Typography variant="body1">
              Are you sure you want to delete the{" "}
              <strong>
                {currentCommodity?.CommodityName ?? `#${layer.LayerNumber}`}
              </strong>{" "}
              layer? This action cannot be undone.
            </Typography>
          )}
        </MesfModal.Content>
        <MesfModal.Actions sx={{ p: "20px 30px" }}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item md={3} xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item md={3} xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={handleDelete}
                disabled={deleteLayerMutation.isPending}
              >
                DELETE
              </Button>
            </Grid>
          </Grid>
        </MesfModal.Actions>
      </MesfModal>
      <ErrorModal error={error} onHide={() => setError("")} />
    </div>
  );
};
